import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { parseCountable, formatCountable } from '@/lib/count-up';
import { motionAllowed, easeOutCubic } from '@/lib/motion';

const DURATION = 700;

/**
 * A stat tile's number, counting up from zero the first time it is seen.
 *
 * React renders the real string and owns it; the tween only rewrites the
 * text node's value frame by frame (no re-render per frame) and its last
 * frame writes the original string back, so what stays on screen is exactly
 * what the page computed. Anything that is not one plain amount (a dash,
 * "2 of 5", an element) renders as it is.
 *
 * The box never changes size while it counts: .cb-count (App.css) lays an
 * invisible copy of the final string under the moving one, so the chip
 * beside a number never slides. The count waits until the number is on
 * screen; a tile below the fold keeps its real value until it scrolls in.
 * Once per mount: a refetch that changes the value just shows the new one.
 * No motion under prefers-reduced-motion, and none where there is no
 * IntersectionObserver (tests, old browsers).
 */
export function CountUp({ value }) {
  const text = typeof value === 'number' && Number.isFinite(value) ? String(value) : value;
  const parsed = useMemo(() => parseCountable(text), [text]);
  const ref = useRef(null);
  const started = useRef(false);

  useLayoutEffect(() => {
    const el = ref.current;
    const node = el?.firstChild;
    if (!parsed || started.current || !node || node.nodeType !== 3) return undefined;
    if (typeof window.IntersectionObserver !== 'function' || typeof window.requestAnimationFrame !== 'function' || !motionAllowed()) return undefined;

    let raf = 0;
    const zero = formatCountable(parsed, 0);
    const run = () => {
      started.current = true;
      node.nodeValue = zero;
      const t0 = performance.now();
      const step = (now) => {
        const p = Math.min(1, Math.max(0, (now - t0) / DURATION));
        node.nodeValue = p >= 1 ? parsed.text : formatCountable(parsed, parsed.target * easeOutCubic(p));
        if (p < 1) raf = window.requestAnimationFrame(step);
      };
      raf = window.requestAnimationFrame(step);
    };

    // Zero before the first paint, so a tile already on screen never shows
    // its final value and then drops to 0. The observer's first answer comes
    // right after: on screen, count; off screen, put the real value back and
    // wait (threshold 0, so nothing visible ever flips).
    node.nodeValue = zero;
    let io = new window.IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        io = null;
        run();
      } else {
        node.nodeValue = parsed.text;
      }
    }, { threshold: 0 });
    io.observe(el);

    // No restore here: this runs on unmount, or after React has already
    // written the next value into the node.
    return () => {
      window.cancelAnimationFrame(raf);
      if (io) io.disconnect();
    };
  }, [parsed]);

  if (!parsed) return value ?? null;
  return (
    <span className="cb-count" data-final={text}>
      <span ref={ref}>{text}</span>
    </span>
  );
}

export default CountUp;
